import React from 'react';
import { Location, Phone, Calendar, ArrowRight } from '../components/icons';
import { SlotMachineButton } from '../components/SlotMachineButton';
import { ContactForm } from '../components/ContactForm';
import { ContactImageModule } from '../components/ContactImageModule';
import { useContactSection } from '../hooks/useContactSection';

function Contact(): React.JSX.Element {
  const { data: contactData, loading, error } = useContactSection();

  const getIcon = (iconType: string) => {
    switch (iconType) {
      case 'location':
        return <Location size={24} />;
      case 'phone':
        return <Phone size={24} />;
      case 'calendar':
        return <Calendar size={24} />;
      default:
        return <Location size={24} />;
    }
  };

  // Function to render address with clickable link
  const renderAddress = (addressText: string) => {
    const lines = addressText.split('\n');
    return lines.map((line, index) => {
      // Check if this line contains clickable address information (street or postal code/city)
      if (line.trim() === 'Kennemerstraatweg 464' || line.trim() === '1851 NG Heiloo') {
        const googleMapsUrl = 'https://maps.google.com/?q=Kennemerstraatweg+464,+1851+NG+Heiloo,+Netherlands';
        return (
          <div key={index} className="contact-text">
            <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="contact-section-link">
              {line}
            </a>
          </div>
        );
      }
      // Check if this line should be styled like a link but not clickable
      if (line.trim() === 'Kamer 1.28') {
        return (
          <div key={index} className="contact-text">
            <span className="contact-section-link">
              {line}
            </span>
          </div>
        );
      }
      return (
        <div key={index} className="contact-text">
          {line}
        </div>
      );
    });
  };

  // Function to render phone number with clickable link
  const renderPhoneNumber = (phoneText: string) => {
    const lines = phoneText.split('\n');
    return lines.map((line, index) => {
      // Check if this line contains the phone number (072 5338718)
      if (line.includes('072 5338718')) {
        const phoneUrl = 'tel:0725338718';
        return (
          <div key={index} className="contact-text">
            <a href={phoneUrl} className="contact-section-link">
              {line}
            </a>
          </div>
        );
      }
      return (
        <div key={index} className="contact-text">
          {line}
        </div>
      );
    });
  };

  // Function to render WhatsApp number with clickable link
  const renderWhatsAppNumber = (whatsappText: string) => {
    const lines = whatsappText.split('\n');
    return lines.map((line, index) => {
      // Check if this line contains the WhatsApp number (06 55487003)
      if (line.includes('06 55487003')) {
        const whatsappUrl = 'https://wa.me/31655487003';
        return (
          <div key={index} className="contact-text">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="contact-section-link">
              {line}
            </a>
          </div>
        );
      }
      return (
        <div key={index} className="contact-text">
          {line}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="contact-loading">
        Laden...
      </div>
    );
  }

  if (error || !contactData) {
    // Fallback data that matches the image exactly
    const fallbackData = {
      title: 'Contact',
      subtitle: 'Heb je een vraag of wil je direct een afspraak maken? Je bereikt ons snel en makkelijk via WhatsApp, telefoon of ons formulier.',
      appointmentText: 'Gemakkelijk een afspraak maken via onze online tool.',
      phoneSection: {
        title: 'Telefoon',
        description: '072 5338718\n\nMaandag tot en met vrijdag van 09:00 tot 17:30'
      },
      whatsappSection: {
        title: 'Whatsapp',
        description: '06 55487003\n\nStuur ons gemakkelijk een berichtje via Whatsapp.'
      },
      addressSection: {
        title: 'Adres',
        description: 'Kennemerstraatweg 464\nKamer 1.28\n1851 NG Heiloo\n\nUitsluitend op afspraak.\nParkeren is gratis op ons terrein.'
      },
      appointmentButton: {
        text: 'Afspraak maken',
        url: '/behandelingen'
      }
    };

    return (
      <main>
        <section className="contact-section">
          {/* Left Column - Main Contact Info */}
          <div className="contact-section-left">
            {/* Title */}
            <h1>
              {fallbackData.title}
            </h1>

            {/* Subtitle */}
            <p>
              {fallbackData.subtitle}
            </p>

            {/* Appointment Text */}
            <p>
              {fallbackData.appointmentText}
            </p>

            <div className="contact-section-button">
              {/* Appointment Button */}
              <SlotMachineButton
                text={fallbackData.appointmentButton.text}
                icon={<ArrowRight size={16} />}
                href={fallbackData.appointmentButton.url}
                className="contact-appointment-button"
                variant="primary"
              />
            </div>
          </div>

          {/* Right Column - Contact Details */}
          <div className="contact-section-right">
            {/* Phone Section */}
            <div>
              <h4>
                {fallbackData.phoneSection.title}
              </h4>
              <div>
                {renderPhoneNumber(fallbackData.phoneSection.description)}
              </div>
            </div>

            {/* WhatsApp Section */}
            <div>
              <h4>
                {fallbackData.whatsappSection.title}
              </h4>
              <div>
                {renderWhatsAppNumber(fallbackData.whatsappSection.description)}
              </div>
            </div>

            {/* Address Section */}
            <div>
              <h4>
                {fallbackData.addressSection.title}
              </h4>
              <div>
                {renderAddress(fallbackData.addressSection.description)}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Image Module Section */}
        <ContactImageModule />

        {/* Contact Form Section (title/description rendered by ContactForm) */}
        <section className="contact-form-page-section">
          <div className="contact-form-wrapper">
            <div className="contact-form-section">
              <ContactForm />
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="contact-section">
        {/* Left Column - Main Contact Info */}
        <div>
          {/* Title */}
          <h1>
            {contactData.title}
          </h1>

          {/* Subtitle */}
          <p>
            {contactData.subtitle}
          </p>

          {/* Appointment Text */}
          {contactData.appointmentText && (
            <p>
              {contactData.appointmentText}
            </p>
          )}
          <div className="contact-section-button">
          {/* New Primary Button */}
          {contactData.appointmentButton && (
            <SlotMachineButton
              text={contactData.appointmentButton.text}
              icon={<ArrowRight size={16} />}
              href={contactData.appointmentButton.url}
              className="btn btn-primary"
              variant={
                contactData.appointmentButton.variant === 'outline'
                  ? 'secondary'
                  : (contactData.appointmentButton.variant as 'primary' | 'secondary' | 'tertiary') || 'primary'
              }
            />
          )}
          </div>
        </div>

        {/* Right Column - Contact Details */}
        <div>
          {/* Phone Section */}
          {contactData.phoneSection && (
            <div>
              <h4>
                {contactData.phoneSection.title}
              </h4>
              <div>
                {renderPhoneNumber(contactData.phoneSection.description)}
              </div>
              {/* Optional extra description for phone section */}
              {contactData.phoneSection.extraDescription && (
                <div className="contact-extra-text">
                  {contactData.phoneSection.extraDescription.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* WhatsApp Section */}
          {contactData.whatsappSection && (
            <div>
              <h4>
                {contactData.whatsappSection.title}
              </h4>
              <div>
                {renderWhatsAppNumber(contactData.whatsappSection.description)}
              </div>
              {/* Optional extra description for WhatsApp section */}
              {contactData.whatsappSection.extraDescription && (
                <div className="contact-extra-text">
                  {contactData.whatsappSection.extraDescription.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Address Section */}
          {contactData.addressSection && (
            <div>
              <h4>
                {contactData.addressSection.title}
              </h4>
              <div>
                {renderAddress(contactData.addressSection.description)}
              </div>
              {/* Optional extra description for address section */}
              {contactData.addressSection.extraDescription && (
                <div className="contact-extra-text">
                  {contactData.addressSection.extraDescription.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Contact Image Module Section */}
      <ContactImageModule />

      {/* Contact Form Section (title/description rendered by ContactForm) */}
      <section className="contact-form-page-section">
        <div className="contact-form-wrapper">
          <div className="contact-form-section">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}

export default Contact; 